import React, { useState } from 'react'
import { useVehicles } from '../contexts/VehicleContext'
import MacStyleVehiclePanel from './MacStyleVehiclePanel'
import FleetMap from './FleetMap'
import SpeedChart from './SpeedChart'
import EventChart from './EventChart'
import { Vehicle } from '../types'

// Mac-style window component
interface MacWindowProps {
  title: string
  children: React.ReactNode
  onClose?: () => void
  onMinimize?: () => void
  initialPosition?: { x: number, y: number }
  width?: number
  height?: number
  isMinimized?: boolean
  className?: string
}

const MacWindow: React.FC<MacWindowProps> = ({
  title,
  children,
  onClose,
  onMinimize,
  initialPosition = { x: 20, y: 80 },
  width = 400,
  height = 500,
  isMinimized = false,
  className = ''
}) => {
  const [position, setPosition] = useState(initialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const windowRef = React.useRef<HTMLDivElement>(null)

  // Handle drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('drag-handle')) {
      setIsDragging(true)
      const rect = windowRef.current?.getBoundingClientRect()
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        })
      }
    }
  }

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: Math.max(0, e.clientX - dragOffset.x),
          y: Math.max(0, e.clientY - dragOffset.y)
        })
      }
    }

    const handleMouseUp = () => setIsDragging(false)

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset])

  if (isMinimized) {
    return (
      <div
        ref={windowRef}
        className="fixed z-50 bg-gray-800 rounded-lg shadow-lg cursor-pointer px-3 py-1"
        style={{ left: position.x, top: position.y }}
        onClick={onMinimize}
      >
        <div className="text-white text-xs font-medium">{title}</div>
      </div>
    )
  }

  return (
    <div
      ref={windowRef}
      className={`fixed z-40 bg-white rounded-xl shadow-2xl border border-gray-200 ${className}`}
      style={{ 
        left: position.x, 
        top: position.y,
        width: `${width}px`,
        height: `${height}px`,
        transform: isDragging ? 'scale(1.01)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.2s ease'
      }}
    >
      {/* Mac-style title bar */}
      <div 
        className="drag-handle flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl border-b border-gray-200 cursor-move"
        onMouseDown={handleMouseDown}
      >
        {/* Traffic light buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={onClose}
            className="w-3 h-3 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
            title="Close"
          />
          <button
            onClick={onMinimize}
            className="w-3 h-3 bg-yellow-500 rounded-full hover:bg-yellow-600 transition-colors"
            title="Minimize"
          />
          <button
            className="w-3 h-3 bg-green-500 rounded-full hover:bg-green-600 transition-colors"
            title="Maximize"
          />
        </div>

        {/* Window title */}
        <div className="flex-1 text-center">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        </div>

        {/* Spacer for symmetry */}
        <div className="w-16"></div>
      </div>

      {/* Window content */}
      <div className="flex-1 overflow-hidden rounded-b-xl">
        {children}
      </div>
    </div>
  )
}

const MacStyleFleetDashboard: React.FC = () => {
  const { state, selectVehicle } = useVehicles()
  const [timeRange, setTimeRange] = useState(2160)
  
  // Window visibility states
  const [showVehiclePanel, setShowVehiclePanel] = useState(true)
  const [showSpeedChart, setShowSpeedChart] = useState(false)
  const [showEventChart, setShowEventChart] = useState(false)
  
  // Window minimized states
  const [vehiclePanelMinimized, setVehiclePanelMinimized] = useState(false)
  const [speedChartMinimized, setSpeedChartMinimized] = useState(false)
  const [eventChartMinimized, setEventChartMinimized] = useState(false)

  const handleVehicleSelect = (vehicle: Vehicle) => {
    selectVehicle(vehicle)
    // Auto-open analytics when vehicle is selected
    if (vehicle && !showSpeedChart && !showEventChart) {
      setShowSpeedChart(true)
    }
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
      {/* Mac-style menu bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">F</span>
              </div>
              <span className="font-semibold text-gray-800">Fleet Portal</span>
            </div>
            
            {/* Menu items */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setShowVehiclePanel(true)}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Vehicles
              </button>
              <button
                onClick={() => setShowSpeedChart(true)}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                disabled={!state.selectedVehicle}
              >
                Speed
              </button>
              <button
                onClick={() => setShowEventChart(true)}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                disabled={!state.selectedVehicle}
              >
                Events
              </button>
            </div>
          </div>

          {/* Status indicators */}
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>{state.vehicles.filter(v => v.status === 'moving').length} Moving</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>{state.vehicles.filter(v => v.status === 'stopped').length} Stopped</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <span>{state.vehicles.filter(v => v.status === 'offline').length} Offline</span>
            </div>
          </div>
        </div>
      </div>

      {/* Full-screen map background */}
      <div className="flex-1 relative bg-gray-200">
        <div className="absolute inset-0 w-full h-full">
          <FleetMap
            vehicles={state.vehicles}
            selectedVehicle={state.selectedVehicle}
            onVehicleSelect={handleVehicleSelect}
            timeRange={timeRange}
          />
        </div>
        
        {/* Map status indicator */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg px-3 py-2 text-sm">
          🗺️ Google Maps | {state.vehicles.length} vehicles
        </div>

        {/* Floating window controls */}
        <div className="absolute top-4 right-4 space-y-2">
          <button
            onClick={() => setShowVehiclePanel(true)}
            className="bg-white rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            🚗 Show Vehicles
          </button>
          {state.selectedVehicle && (
            <>
              <button
                onClick={() => setShowSpeedChart(true)}
                className="bg-white rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors block w-full"
              >
                📈 Speed Analysis
              </button>
              <button
                onClick={() => setShowEventChart(true)}
                className="bg-white rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors block w-full"
              >
                🛑 Stop Events
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mac-style floating windows */}
      {showVehiclePanel && !vehiclePanelMinimized && (
        <MacWindow
          title="Fleet Vehicles"
          onClose={() => setShowVehiclePanel(false)}
          onMinimize={() => setVehiclePanelMinimized(true)}
          initialPosition={{ x: 20, y: 80 }}
          width={380}
          height={600}
        >
          <MacStyleVehiclePanel
            vehicles={state.vehicles}
            selectedVehicle={state.selectedVehicle}
            onVehicleSelect={handleVehicleSelect}
            onClose={() => setShowVehiclePanel(false)}
          />
        </MacWindow>
      )}

      {showSpeedChart && state.selectedVehicle && !speedChartMinimized && (
        <MacWindow
          title={`Speed Analysis - ${state.selectedVehicle.name}`}
          onClose={() => setShowSpeedChart(false)}
          onMinimize={() => setSpeedChartMinimized(true)}
          initialPosition={{ x: 420, y: 80 }}
          width={600}
          height={400}
        >
          <SpeedChart 
            vehicle={state.selectedVehicle} 
            timeRange={timeRange}
          />
        </MacWindow>
      )}

      {showEventChart && state.selectedVehicle && !eventChartMinimized && (
        <MacWindow
          title={`Event Analysis - ${state.selectedVehicle.name}`}
          onClose={() => setShowEventChart(false)}
          onMinimize={() => setEventChartMinimized(true)}
          initialPosition={{ x: 420, y: 500 }}
          width={700}
          height={500}
        >
          <EventChart 
            vehicle={state.selectedVehicle} 
            timeRange={timeRange}
          />
        </MacWindow>
      )}

      {/* Dock-style minimized windows */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {vehiclePanelMinimized && (
          <button
            onClick={() => setVehiclePanelMinimized(false)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm hover:bg-gray-700 transition-colors"
          >
            🚗 Vehicles
          </button>
        )}
        {speedChartMinimized && (
          <button
            onClick={() => setSpeedChartMinimized(false)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm hover:bg-gray-700 transition-colors"
          >
            📈 Speed
          </button>
        )}
        {eventChartMinimized && (
          <button
            onClick={() => setEventChartMinimized(false)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm hover:bg-gray-700 transition-colors"
          >
            🛑 Events
          </button>
        )}
      </div>
    </div>
  )
}

export default MacStyleFleetDashboard
